/**
 * NewProjectModal — single project modal used in both modes:
 *
 *   mode='new'  → "+ New project" flow. Empty name, optional KB. On
 *                 submit, parent receives `{ name, kbFiles[] }` and is
 *                 responsible for create + KB uploads + navigation.
 *   mode='edit' → opened by clicking a project row / `⋯` kebab. Name
 *                 input pre-fills from `initialName`. Submit calls
 *                 `onUpdate({ name, kbFiles[] })` so the parent can
 *                 PUT the rename + upload any newly-attached KB. A
 *                 Delete project button surfaces in the footer.
 *
 * KB list (existing per-consultant docs) loads in both modes. Removing
 * an existing KB doc hits the delete endpoint immediately (the KB is a
 * shared resource — the change applies to every project).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import FileTypeBadge from './FileTypeBadge';
import { formatBytes } from './chat/renderMessage';
import { listKnowledgeBase, deleteKnowledgeBaseFile, listKbDocuments, requestKbUploadUrl, notifyKbUploadComplete, deleteKbDocument, getDesignTemplate, requestTemplateUploadUrl, compileDesignTemplate, deleteDesignTemplate, listDesigns } from '../services/api';
import { confirm as confirmDialog } from './ConfirmDialog';
import { useAuth } from '../AuthContext';
import CustomerNameField from './CustomerNameField';

// ─── KnowledgeBaseSection ─────────────────────────────────────────────────────
// Inline component (< 150 lines) — manages the /kb/documents contract:
//   list → upload (presign + PUT + upload-complete) → delete
// Polls every 5s while any row has ingestStatus === 'indexing', capped at
// 24 ticks (~2 min). Always clears the interval on unmount.
const MAX_POLL_TICKS = 24;
const POLL_INTERVAL_MS = 5000;

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

// ─── DesignTemplateSection ──────────────────────────────────────────────────
// One DOCX per consultant, stored at templates/{consultantId}/...docx in the
// workflows bucket. Replacing it overrides the global template; deleting
// reverts to global. Not part of KB — agent reads it directly from S3.
function DesignTemplateSection({ consultantId, disabled }) {
  const [meta, setMeta] = useState({ override: null, global: null });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const fetchMeta = useCallback(async () => {
    if (!consultantId) return;
    try {
      const data = await getDesignTemplate(consultantId);
      setMeta(data || { override: null, global: null });
      setError('');
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load template metadata.');
    }
  }, [consultantId]);

  useEffect(() => {
    if (!consultantId) return;
    setLoading(true);
    fetchMeta().finally(() => setLoading(false));
  }, [consultantId, fetchMeta]);

  const handleUpload = async (file) => {
    if (!file || !consultantId) return;
    setBusy(true);
    setError('');
    try {
      const contentType = file.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const { uploadUrl } = await requestTemplateUploadUrl(consultantId, contentType);
      const putResp = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': contentType },
      });
      if (!putResp.ok) throw new Error(`S3 PUT failed: ${putResp.status}`);

      // Compile the uploaded DOCX to markdown
      try {
        const compileResult = await compileDesignTemplate(consultantId);
        console.log(`[template] Compiled: ${compileResult.sections} sections, ${compileResult.headings} headings, ${compileResult.tables} tables`);
      } catch (compileErr) {
        const status = compileErr?.response?.status;
        const errData = compileErr?.response?.data;
        if (status === 422 && errData?.reasons) {
          // Validation failed — show the reasons
          setError(`Template validation failed: ${errData.reasons.join('; ')}`);
          await fetchMeta();
          return;
        } else if (status === 404) {
          setError('No uploaded template found to compile.');
        } else {
          setError(errData?.error || compileErr?.message || 'Compile failed.');
        }
        await fetchMeta();
        return;
      }

      await fetchMeta();
    } catch (e) {
      setError(e?.message || 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleRevertToGlobal = async () => {
    const ok = await confirmDialog({
      title: 'Remove custom template',
      message: 'Revert to the global AJO design document template? Your custom template will be deleted.',
      confirmLabel: 'Revert',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await deleteDesignTemplate(consultantId);
      await fetchMeta();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to revert template.');
    } finally {
      setBusy(false);
    }
  };

  // Show only the user's OWN template, never the global fallback —
  // matches the KB section's behavior (which never surfaces system /
  // global KB entries). When the user hasn't uploaded an override, the
  // empty state explains that a built-in default is in effect; the
  // global template file itself isn't shown as a chip.
  const usingOverride = Boolean(meta.override);

  return (
    <div className="modal-field">
      <span className="modal-label">
        Design Document Template
        <span className="modal-hint">
          DOCX skeleton (headings, sections, subsections) every generated design follows. Upload your own to use it for every design in this account.
        </span>
      </span>
      <div className="modal-drop modal-drop-list">
        {loading && <div className="modal-section-empty">Loading…</div>}
        {!loading && error && <div className="modal-error">{error}</div>}
        {!loading && usingOverride && meta.override && (
          <div className="modal-file-chip">
            <FileTypeBadge name={meta.override.name} />
            <div className="modal-file-info">
              <div className="modal-file-name">{meta.override.name}</div>
              <div className="modal-file-meta">
                {typeof meta.override.sizeBytes === 'number' ? formatBytes(meta.override.sizeBytes) : ''}
                <span className="modal-hint" style={{ marginLeft: 8, color: '#1a7f3c' }}>
                  custom (this account)
                </span>
              </div>
            </div>
            <button
              type="button"
              className="modal-file-remove"
              onClick={handleRevertToGlobal}
              disabled={disabled || busy}
              aria-label="Remove custom template"
              title="Remove (revert to built-in default)"
            >×</button>
          </div>
        )}
        {!loading && !usingOverride && (
          <div className="modal-section-empty" style={{ padding: '8px 4px' }}>
            No custom template uploaded — using the built-in default.
          </div>
        )}
        <button
          type="button"
          className="modal-drop-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || busy}
        >
          {busy ? 'Uploading…' : (usingOverride ? '+ Replace template' : '+ Upload custom template')}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept=".docx"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleUpload(f);
            if (e.target) e.target.value = '';
          }}
        />
      </div>
    </div>
  );
}


function KnowledgeBaseSection({ consultantId, disabled }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const tickRef = useRef(0);

  const clearPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    tickRef.current = 0;
  };

  const fetchDocs = useCallback(async () => {
    if (!consultantId) return;
    try {
      const data = await listKbDocuments(consultantId);
      const list = Array.isArray(data?.documents) ? data.documents : [];
      setDocs(list);
      setError('');
      // Stop polling when nothing is still indexing.
      if (!list.some((d) => d.ingestStatus === 'indexing')) {
        clearPoll();
      }
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load KB documents.');
    }
  }, [consultantId]);

  const startPoll = useCallback(() => {
    clearPoll();
    tickRef.current = 0;
    pollRef.current = setInterval(async () => {
      tickRef.current += 1;
      if (tickRef.current >= MAX_POLL_TICKS) {
        clearPoll();
        return;
      }
      await fetchDocs();
    }, POLL_INTERVAL_MS);
  }, [fetchDocs]);

  // Initial load whenever modal opens (consultantId changes).
  useEffect(() => {
    if (!consultantId) return;
    setLoading(true);
    fetchDocs().finally(() => setLoading(false));
    return () => clearPoll();
  }, [consultantId, fetchDocs]);

  const handleUpload = async (file) => {
    if (!file || !consultantId) return;
    setUploading(true);
    setError('');
    // Optimistic insert with indexing status.
    const optimisticDoc = {
      name: file.name,
      s3Key: `__optimistic__${Date.now()}`,
      sizeBytes: file.size,
      lastModified: new Date().toISOString(),
      ingestStatus: 'indexing',
    };
    setDocs((prev) => [optimisticDoc, ...prev]);
    try {
      const { uploadUrl, s3Key } = await requestKbUploadUrl(consultantId, file.name, file.type || 'application/octet-stream');
      const putResp = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      if (!putResp.ok) throw new Error(`S3 PUT failed: ${putResp.status}`);
      await notifyKbUploadComplete(s3Key);
      // Replace the optimistic row with the real s3Key; polling will update status.
      setDocs((prev) => prev.map((d) =>
        d.s3Key === optimisticDoc.s3Key ? { ...d, s3Key } : d
      ));
      startPoll();
    } catch (e) {
      setError(e?.message || 'Upload failed.');
      setDocs((prev) => prev.filter((d) => d.s3Key !== optimisticDoc.s3Key));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (doc) => {
    const ok = await confirmDialog({
      title: 'Remove KB document',
      message: `Remove "${doc.name}"? It will be removed from every project for this account.`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    // Optimistic: mark as indexing (de-indexing in progress).
    setDocs((prev) => prev.map((d) => d.s3Key === doc.s3Key ? { ...d, ingestStatus: 'indexing' } : d));
    try {
      await deleteKbDocument(doc.s3Key);
      startPoll();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to delete document.');
      // Revert optimistic update.
      setDocs((prev) => prev.map((d) => d.s3Key === doc.s3Key ? { ...d, ingestStatus: doc.ingestStatus } : d));
    }
  };

  return (
    <div className="modal-section kb-section">
      <div className="modal-section-title">Knowledge Base</div>
      <div className="modal-section-hint">
        Shared across every project for this account — templates, prior approved designs, AJO patterns.
      </div>
      {loading && <div className="modal-section-empty">Loading…</div>}
      {!loading && error && <div className="modal-error" style={{ marginBottom: 8 }}>{error}</div>}
      {!loading && docs.length === 0 && !error && (
        <div className="modal-section-empty">
          No documents in your knowledge base. Upload templates and style guides for the agent to reference.
        </div>
      )}
      {!loading && docs.length > 0 && (
        <ul className="modal-file-list">
          {docs.map((doc) => (
            <li className="modal-file-chip" key={doc.s3Key}>
              <FileTypeBadge name={doc.name} />
              <div className="modal-file-info">
                <div className="modal-file-name">{doc.name}</div>
                <div className="modal-file-meta">
                  {typeof doc.sizeBytes === 'number' ? formatBytes(doc.sizeBytes) : ''}
                  {doc.ingestStatus && (
                    <span
                      className="modal-hint"
                      style={{
                        marginLeft: 8,
                        color: doc.ingestStatus === 'indexed' ? '#1a7f3c' : '#b45309',
                      }}
                    >
                      {doc.ingestStatus}
                    </span>
                  )}
                  {doc.lastModified && (
                    <span className="modal-hint" style={{ marginLeft: 8 }}>
                      {formatDate(doc.lastModified)}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="modal-file-remove"
                onClick={() => handleDelete(doc)}
                disabled={disabled || uploading || doc.ingestStatus === 'indexing'}
                aria-label={`Remove ${doc.name} from KB`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        className="modal-drop-btn"
        style={{ marginTop: 8 }}
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled || uploading}
      >
        {uploading ? 'Uploading…' : '+ Upload doc'}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept=".pdf,.docx,.doc,.html,.htm,.txt,.md,.json,.xlsx,.xls,.csv"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleUpload(file);
          if (e.target) e.target.value = '';
        }}
      />
    </div>
  );
}

const KB_ACCEPT = '.pdf,.docx,.doc,.html,.htm,.txt,.md,.json,.xlsx,.xls,.csv';

export default function NewProjectModal({
  open,
  mode = 'new',
  initialName = '',
  initialCustomerName = '',
  designs = null,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
  busy = false,
}) {
  const isEdit = mode === 'edit';
  // consultantId comes from the shared Okta auth context — same source
  // used by every other call site (NewChat, ProjectTree). No prop-drilling.
  const { user } = useAuth();
  const consultantId = user?.email || null;
  const [name, setName] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [allDesigns, setAllDesigns] = useState([]);
  // Knowledge base: per-consultant docs shared across every project.
  // `kbExisting` are docs already in S3 (loaded from /kb on open);
  // `kbNew` are files staged in this modal session that will upload at
  // submit time. Removing an `kbExisting` entry deletes it from S3
  // immediately (the user is editing a shared resource).
  const [kbExisting, setKbExisting] = useState([]);
  const [kbNew, setKbNew] = useState([]);
  const [kbLoading, setKbLoading] = useState(false);
  const [kbError, setKbError] = useState('');
  const [error, setError] = useState('');

  const nameRef = useRef(null);
  const kbInputRef = useRef(null);

  // Reset state every time the modal opens — prevents the previous
  // attempt's name / files leaking into a fresh project flow.
  useEffect(() => {
    if (!open) return;
    setName(isEdit ? (initialName || '') : '');
    setCustomerName(isEdit ? (initialCustomerName || '') : '');
    setKbNew([]);
    setError('');
    setKbError('');
    setKbLoading(true);
    let cancelled = false;
    listKnowledgeBase()
      .then((r) => { if (!cancelled) { setKbExisting(Array.isArray(r?.files) ? r.files : []); setKbLoading(false); } })
      .catch((e) => { if (!cancelled) { setKbError(e?.message || 'Failed to load KB'); setKbLoading(false); } });
    // Autocomplete options come from the parent when it already has the
    // designs list; only fetch as a fallback.
    if (consultantId && !designs) {
      listDesigns(consultantId)
        .then((data) => { if (!cancelled) setAllDesigns(data?.designs || []); })
        .catch(() => {});
    }
    // Defer focus until the dialog has actually painted.
    setTimeout(() => nameRef.current?.focus(), 50);
    return () => { cancelled = true; };
  }, [open, isEdit, initialName, initialCustomerName, consultantId, designs]);

  // Esc closes when not busy uploading.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const addKb = (files) => {
    const incoming = Array.from(files || []).filter(Boolean);
    if (!incoming.length) return;
    setKbNew((prev) => {
      const seen = new Set([
        ...prev.map((f) => f.name.toLowerCase()),
        ...kbExisting.map((f) => (f.name || '').toLowerCase()),
      ]);
      const merged = [...prev];
      for (const f of incoming) {
        const k = f.name.toLowerCase();
        if (!seen.has(k)) {
          seen.add(k);
          merged.push(f);
        }
      }
      return merged;
    });
  };
  const removeKbNew = (idx) => setKbNew((prev) => prev.filter((_, i) => i !== idx));
  const removeKbExisting = async (s3Key) => {
    // Editing a shared resource — confirm so a misclick doesn't wipe a
    // doc the user (or a colleague) wants to keep.
    const ok = await confirmDialog({
      title: 'Remove KB doc',
      message: 'Remove this KB doc? It will be removed from every project for this account.',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteKnowledgeBaseFile(s3Key);
      setKbExisting((prev) => prev.filter((f) => f.s3Key !== s3Key));
    } catch (e) {
      setKbError(e?.response?.data?.error || e?.message || 'Failed to remove KB doc.');
    }
  };

  const submit = () => {
    if (busy) return;
    const trimmed = name.trim();
    const trimmedCustomer = customerName.trim();
    if (!trimmed) {
      setError('Project name is required.');
      nameRef.current?.focus();
      return;
    }
    if (!trimmedCustomer) {
      setError('Customer name is required.');
      return;
    }
    setError('');
    if (isEdit) {
      onUpdate?.({ name: trimmed, customerName: trimmedCustomer, kbFiles: kbNew });
    } else {
      onCreate?.({ name: trimmed, customerName: trimmedCustomer, kbFiles: kbNew });
    }
  };

  return (
    <div
      className="modal-backdrop modal-backdrop-top"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose?.();
      }}
    >
      <div className="modal-card" role="dialog" aria-modal="true" aria-labelledby="new-project-title">
        <div className="modal-header">
          <h2 id="new-project-title">{isEdit ? 'Project' : 'New project'}</h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          <label className="modal-field">
            <span className="modal-label">Project name</span>
            <input
              ref={nameRef}
              type="text"
              className="modal-input"
              placeholder="e.g. Luma Birthday Coupon"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              disabled={busy}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
            />
          </label>

          <div className="modal-field">
            <span className="modal-label">Customer name</span>
            <CustomerNameField
              value={customerName}
              onChange={setCustomerName}
              required
              designs={designs || allDesigns}
              disabled={busy}
              placeholder="e.g. Luma"
            />
          </div>

          <div className="modal-field">
            <span className="modal-label">
              Knowledge base
              <span className="modal-hint">shared across every project for this account — templates, prior approved designs, AJO patterns (optional)</span>
            </span>
            <div className="modal-drop modal-drop-list">
              {kbLoading && <div className="modal-section-empty">Loading existing KB…</div>}
              {!kbLoading && kbError && <div className="modal-error">{kbError}</div>}
              {!kbLoading && kbExisting.map((f) => (
                <div className="modal-file-chip" key={f.s3Key}>
                  <FileTypeBadge name={f.name} />
                  <div className="modal-file-info">
                    <div className="modal-file-name">{f.name}</div>
                    <div className="modal-file-meta">
                      {typeof f.size === 'number' ? formatBytes(f.size) : ''}
                      <span className="modal-hint" style={{ marginLeft: 8 }}>in KB</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="modal-file-remove"
                    onClick={() => removeKbExisting(f.s3Key)}
                    disabled={busy}
                    aria-label={`Remove ${f.name} from KB`}
                  >×</button>
                </div>
              ))}
              {kbNew.map((f, i) => (
                <div className="modal-file-chip" key={`kb-new-${f.name}-${i}`}>
                  <FileTypeBadge name={f.name} />
                  <div className="modal-file-info">
                    <div className="modal-file-name">{f.name}</div>
                    <div className="modal-file-meta">
                      {formatBytes(f.size)}
                      <span className="modal-hint" style={{ marginLeft: 8 }}>will upload to KB</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="modal-file-remove"
                    onClick={() => removeKbNew(i)}
                    disabled={busy}
                    aria-label={`Remove ${f.name}`}
                  >×</button>
                </div>
              ))}
              <button
                type="button"
                className="modal-drop-btn"
                onClick={() => kbInputRef.current?.click()}
                disabled={busy}
              >+ Add to KB</button>
              <input
                ref={kbInputRef}
                type="file"
                hidden
                multiple
                accept={KB_ACCEPT}
                onChange={(e) => {
                  addKb(e.target.files);
                  if (e.target) e.target.value = '';
                }}
              />
            </div>
          </div>

          {error && <div className="modal-error">{error}</div>}

          {/* Design Document Template (per-tenant override of the global
              structural skeleton). Lives below the legacy KB chip list,
              uses the same chip + drop-button format. */}
          <DesignTemplateSection consultantId={consultantId} disabled={busy} />
        </div>

        <div className="modal-footer">
          {isEdit && onDelete && (
            <button
              type="button"
              className="modal-btn modal-btn-danger"
              onClick={onDelete}
              disabled={busy}
              style={{ marginRight: 'auto' }}
              title="Delete this project"
            >
              Delete project
            </button>
          )}
          <button
            type="button"
            className="modal-btn modal-btn-ghost"
            onClick={onClose}
            disabled={busy}
          >
            {isEdit ? 'Close' : 'Cancel'}
          </button>
          <button
            type="button"
            className="modal-btn modal-btn-primary"
            onClick={submit}
            disabled={busy}
          >
            {busy
              ? (isEdit ? 'Saving…' : 'Creating…')
              : (isEdit ? 'Update project' : 'Create project')}
          </button>
        </div>
      </div>
    </div>
  );
}
